// utils/availability.js
import moment from 'moment';
import PropTypes from 'prop-types';

/**
 * Normalizes and structures the availability data
 * @param {Object.<string, DayAvailability>} availability - Raw availability data
 * @returns {Object.<string, DayAvailability>} Normalized availability data
 */
export const normalizeAvailability = (availability = {}) => {
  const normalizedAvailability = {};

  Object.entries(availability).forEach(([day, times]) => {
    if (!times) return;

    // Handle case with single othertime entry
    if (times.othertime?.length === 1) {
      const [otherTime] = times.othertime;

      // If end time is 23:59, treat it as continuous time
      if (otherTime.end === '23:59') {
        normalizedAvailability[day] = {
          start: otherTime.start,
          end: times.end,
          isAvailable: times.isAvailable,
        };
        return;
      }
    }

    normalizedAvailability[day] = times;
  });

  return normalizedAvailability;
};

/**
 * Checks if a given time falls within a time range
 * @param {string} currentTime - Current time in HH:mm format
 * @param {string} startTime - Start time in HH:mm format
 * @param {string} endTime - End time in HH:mm format
 * @returns {boolean} Whether the current time is within range
 */
const isTimeInRange = (currentTime, startTime, endTime) => {
  // For easier comparison, convert times to minutes
  const toMinutes = (time) => {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
  };

  const current = toMinutes(currentTime);
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);

  // Special case: 24-hour or nearly 24-hour
  if (
    startTime === endTime ||
    (startTime === '00:00' && (endTime === '23:59' || endTime === '23:30'))
  ) {
    return true;
  }

  // Handle overnight ranges (end time is earlier than start time)
  if (start > end) {
    // Time is either after start time or before end time
    return current >= start || current <= end;
  }

  // Normal case: start time is before end time
  return current >= start && current <= end;
};

/**
 * Checks if any time range in a set is currently active
 * @param {string} currentTime - Current time in HH:mm format
 * @param {TimeRange[]} timeRanges - Array of time ranges
 * @returns {boolean} Whether any time range is active
 */
const isAnyTimeRangeActive = (currentTime, timeRanges) => {
  return timeRanges.some((timeRange) => isTimeInRange(currentTime, timeRange.start, timeRange.end));
};

/**
 * Gets the availability status for a restaurant or category
 * @param {Object} entity - Restaurant or category object with availability data
 * @param {Object} options - Options for checking status
 * @param {string} [options.type='boolean'] - Return type ('string' | 'boolean')
 * @param {string} [options.date] - Specific date to check (MM/DD/YYYY[ HH:mm])
 * @param {boolean} [options.usePromoAvailability=false] - Whether to use promo availability
 * @returns {boolean|string} Availability status
 */
export const getAvailabilityStatus = (
  entity,
  { type = 'boolean', date = '', usePromoAvailability = false } = {},
) => {
  try {
    if (!entity) return type === 'string' ? 'Closed' : false;

    // ONLY use the uppercase Availability property as in the data
    let availability = entity.Availability;

    // Use promo availability if specified
    if (usePromoAvailability && entity.PromoAvailability) {
      availability = entity.PromoAvailability;
    }

    if (!availability) {
      return type === 'string' ? 'Closed' : false;
    }

    // Determine current day and time
    let currentDay = moment().format('ddd');
    let currentTime = moment().format('HH:mm');

    // Handle specific date if provided
    if (date) {
      const dateFormat = date.includes(' ') ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY';
      const dateMoment = moment(date, dateFormat);
      currentDay = dateMoment.format('ddd');
      currentTime = dateMoment.format('HH:mm');
    }

    // Get availability for current day
    const dayAvailability = availability[currentDay];

    // Check if day exists
    if (!dayAvailability) {
      return type === 'string' ? 'Closed' : false;
    }

    // CRITICAL: First check the isAvailable flag explicitly
    if (dayAvailability.isAvailable === false) {
      return type === 'string' ? 'Closed' : false;
    }

    // Handle almost 24-hour availability (midnight to late night)
    if (
      dayAvailability.start === dayAvailability.end ||
      (dayAvailability.start === '00:00' &&
        (dayAvailability.end === '23:59' || dayAvailability.end === '23:30'))
    ) {
      return type === 'string' ? 'Open' : true;
    }

    // Check primary time range
    const isInPrimaryRange = isTimeInRange(currentTime, dayAvailability.start, dayAvailability.end);

    if (isInPrimaryRange) {
      return type === 'string' ? 'Open' : true;
    }

    // Check additional time ranges if available
    if (dayAvailability.othertime?.length) {
      const isInOtherRange = isAnyTimeRangeActive(currentTime, dayAvailability.othertime);
      if (isInOtherRange) {
        return type === 'string' ? 'Open' : true;
      }
    }

    return type === 'string' ? 'Closed' : false;
  } catch (error) {
    console.error('Error checking availability status:', error);
    return type === 'string' ? 'Closed' : false;
  }
};

/**
 * Gets formatted opening hours for display
 * @param {Object} availability - Availability data
 * @param {string} day - Day to get hours for (e.g., 'Mon', 'Tue')
 * @returns {string} Formatted opening hours
 */
export const getFormattedOpeningHours = (availability, day) => {
  const dayAvailability = availability?.[day];

  if (!dayAvailability?.isAvailable) {
    return 'Closed';
  }

  // 24-hour availability
  if (
    dayAvailability.start === '00:00' &&
    (dayAvailability.end === '23:59' || dayAvailability.end === '23:30')
  ) {
    return 'Open 24 hours';
  }

  // Format primary hours
  let hours = `${formatTime(dayAvailability.start)}-${formatTime(dayAvailability.end)}`;

  // Add additional hours if present
  if (dayAvailability.othertime?.length) {
    const additionalHours = dayAvailability.othertime
      .map((time) => `${formatTime(time.start)}-${formatTime(time.end)}`)
      .join(', ');
    hours += `, ${additionalHours}`;
  }

  return hours;
};

/**
 * Helper function to format time display
 * @param {string} time - Time in HH:mm format
 * @returns {string} Formatted time
 */
const formatTime = (time) => {
  if (time === '23:59' || time === '23:30') {
    return '00:00';
  }
  return time;
};

/**
 * Gets the next opening time
 * @param {Object} availability - Availability data
 * @returns {string} Next opening time message
 */
export const getNextOpeningTime = (availability) => {
  const currentDay = moment().format('ddd');
  const currentTime = moment().format('HH:mm');

  // Check remaining times today
  const todayAvailability = availability[currentDay];
  if (todayAvailability?.isAvailable) {
    // Check primary time
    if (moment(todayAvailability.start, 'HH:mm').isAfter(moment(currentTime, 'HH:mm'))) {
      return `Opens today at ${todayAvailability.start}`;
    }

    // Check other times
    const laterTime = todayAvailability.othertime?.find((time) =>
      moment(time.start, 'HH:mm').isAfter(moment(currentTime, 'HH:mm')),
    );

    if (laterTime) {
      return `Opens today at ${laterTime.start}`;
    }
  }

  // Check next 7 days
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const currentDayIndex = daysOfWeek.indexOf(currentDay);

  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDay = daysOfWeek[nextDayIndex];
    const nextDayAvailability = availability[nextDay];

    if (nextDayAvailability?.isAvailable) {
      const dayName = i === 1 ? 'tomorrow' : nextDay;
      return `Opens ${dayName} at ${nextDayAvailability.start}`;
    }
  }

  return 'Currently closed';
};

/**
 * Check if a menu category is currently available based on time and availability settings
 * @param {Object} category - The category object with availability information
 * @param {Date} [currentTime=new Date()] - Optional current time (defaults to now)
 * @returns {boolean} - True if the category is available at the given time
 */
export function isCategoryAvailable(category, currentTime = new Date()) {
  // If there's no Availability object, return false
  if (!category.Availability) {
    return false;
  }

  // Get the day of the week as a three-letter abbreviation
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayOfWeek = days[currentTime.getDay()];

  // Get the availability for the current day
  const dayAvailability = category.Availability[dayOfWeek];

  // If there's no availability for this day or isAvailable is explicitly false, return false
  if (!dayAvailability || dayAvailability.isAvailable === false) {
    return false;
  }

  // Parse start and end times
  const startTime = parseTimeString(dayAvailability.start);
  const endTime = parseTimeString(dayAvailability.end);

  // Current time in minutes since midnight
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

  // Special case: if start is 00:00 and end is 23:59/23:30, it's available all day
  if (startTime === 0 && endTime >= 23 * 60 + 30) {
    return true;
  }

  // Check if current time is within the availability window
  return currentMinutes >= startTime && currentMinutes <= endTime;
}

/**
 * Check if a menu category is currently available based on AvailabilityCopy settings
 * @param {Object} category - The category object with AvailabilityCopy information
 * @param {Date} [currentTime=new Date()] - Optional current time (defaults to now)
 * @returns {boolean} - True if the category is available
 */
export function isCategoryProperlyAvailable(category, currentTime = new Date()) {
  // If there's no Availability object, return false
  if (!category.AvailabilityCopy) {
    return false;
  }

  // Get the day of the week as a three-letter abbreviation
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayOfWeek = days[currentTime.getDay()];

  // Get the availability for the current day
  const dayAvailability = category.AvailabilityCopy[dayOfWeek];

  // If isAvailable is explicitly false, return false
  if (dayAvailability?.isAvailable === false) {
    return false;
  }

  return true;
}

// --- Higher-category serving windows -----------------------------------------
// Client-side mirror of the server's isAvailabilityOpenAt (backend
// src/utils/openingHours.js), which is what the order paths enforce. Keeping
// the two in step matters: anything this says is servable but the server does
// not will be accepted into the cart and then rejected at checkout.
//
// Read AvailabilityCopy, not Availability. /highercategory/fetch rewrites
// Availability[today] into a computed "open right now" status before sending
// it, and returns the untouched stored schedule as AvailabilityCopy — so
// Availability cannot answer "is this category served at 19:00 next Tuesday".

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

// Minutes-of-day for a strict "HH:MM" string, or NaN if malformed.
const toMinutesOfDay = (hhmm) => {
  const match = HHMM.exec(String(hhmm));
  return match ? parseInt(match[1], 10) * 60 + parseInt(match[2], 10) : NaN;
};

// Parsed serving windows for one day — empty when the day is missing, switched
// off, or has no well-formed times.
const validWindows = (day) => {
  if (!day || day.isAvailable === false) return [];

  const raw = [{ start: day.start, end: day.end }];
  if (Array.isArray(day.othertime)) {
    day.othertime.forEach((slot) => raw.push(slot));
  }

  const windows = [];
  raw.forEach((window) => {
    const start = toMinutesOfDay(window?.start);
    const end = toMinutesOfDay(window?.end);
    if (!Number.isNaN(start) && !Number.isNaN(end)) {
      windows.push({ start, end });
    }
  });

  return windows;
};

/**
 * Is a Mon..Sun availability schedule open at a given moment?
 *
 * Fail-safe like the server: only returns false when we positively know it is
 * closed — an explicitly switched-off day, or a readable schedule the moment
 * falls outside. A missing or unparseable schedule counts as open, because
 * this gate hides menu items and a false "closed" costs the venue a sale.
 *
 * @param {Object} availability - Mon..Sun schedule
 * @param {Date} [at=new Date()] - the moment being ordered for
 * @returns {boolean}
 */
export const isAvailabilityOpenAt = (availability, at = new Date()) => {
  if (!availability || typeof availability !== 'object') return true;

  const when = at instanceof Date && !Number.isNaN(at.getTime()) ? at : new Date();
  const dayIndex = when.getDay();
  const minutes = when.getHours() * 60 + when.getMinutes();

  const today = availability[DAY_KEYS[dayIndex]];
  const yesterday = availability[DAY_KEYS[(dayIndex + 6) % 7]];
  const todayWindows = validWindows(today);

  // Inside one of today's windows? A window whose end is before its start
  // crosses midnight, so everything from the start onwards counts.
  const openToday = todayWindows.some(({ start, end }) =>
    end >= start ? minutes >= start && minutes <= end : minutes >= start,
  );
  if (openToday) return true;

  // Still inside yesterday's midnight-crossing tail (e.g. 17:00–01:00)?
  const inYesterdayTail = validWindows(yesterday).some(
    ({ start, end }) => end < start && minutes <= end,
  );
  if (inYesterdayTail) return true;

  if (today && today.isAvailable === false) return false;
  if (todayWindows.length > 0) return false;

  // No usable schedule for that day — cannot determine, so do not hide.
  return true;
};

/**
 * When does the venue next open, counted from a moment?
 *
 * Used to tell a guest standing in front of a greyed-out "Order now" tile WHEN
 * they could have used it — a bare "closed" leaves them with nothing to do but
 * guess. getNextOpeningTime above answers the same question but bakes English
 * sentences into the util, so it is unusable on a six-language page; this
 * returns the parts and lets the caller phrase them.
 *
 * Only forward-looking windows count: a venue open 16:00-19:00 is "opens today
 * at 16:00" at 09:00, but at 20:00 it has to roll to tomorrow. A window already
 * running is not an opening at all — callers ask this only while closed, and a
 * midnight-crossing tail from yesterday is likewise somebody else's window.
 *
 * @param {Object} availability - Mon..Sun schedule
 * @param {Date} [at=new Date()] - the moment to count from
 * @returns {{ dayOffset: number, dayKey: string, start: string }|null}
 *   null when no day in the coming week has a usable window — a venue with no
 *   readable schedule at all, where "opens ..." would be a guess.
 */
export const getNextOpeningSlot = (availability, at = new Date()) => {
  if (!availability || typeof availability !== 'object') return null;

  const when = at instanceof Date && !Number.isNaN(at.getTime()) ? at : new Date();
  const dayIndex = when.getDay();
  const minutes = when.getHours() * 60 + when.getMinutes();

  const toHHMM = (value) => {
    const total = Math.max(0, Math.round(value));
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  // Today: only windows that have not started yet.
  const laterToday = validWindows(availability[DAY_KEYS[dayIndex]])
    .filter(({ start }) => start > minutes)
    .sort((a, b) => a.start - b.start)[0];
  if (laterToday) {
    return { dayOffset: 0, dayKey: DAY_KEYS[dayIndex], start: toHHMM(laterToday.start) };
  }

  // The next seven days, earliest window on the first day that has one.
  for (let offset = 1; offset <= 7; offset += 1) {
    const dayKey = DAY_KEYS[(dayIndex + offset) % 7];
    const earliest = validWindows(availability[dayKey]).sort((a, b) => a.start - b.start)[0];
    if (earliest) {
      return { dayOffset: offset, dayKey, start: toHHMM(earliest.start) };
    }
  }

  return null;
};

/**
 * Is a higher category (lunch, dinner, bar list...) being served at a moment?
 *
 * @param {Object} category - higher category from /highercategory/fetch
 * @param {Date} [at=new Date()] - the moment being ordered for
 * @returns {boolean}
 */
export const isHigherCategoryOpenAt = (category, at = new Date()) =>
  isAvailabilityOpenAt(category?.AvailabilityCopy || category?.Availability, at);

/**
 * Is a higher category served AT ALL on the day of a given moment?
 *
 * Deliberately coarser than isHigherCategoryOpenAt: it reads the per-day
 * on/off flag and ignores the serving window. This is what decides whether a
 * category's items are rendered, because "the venue switched this menu off for
 * the day" is an explicit statement, whereas "we are between lunch and dinner"
 * is not a reason to blank the page — guests browse ahead of service, and a
 * venue's window edges are often approximate. Ordering is still held to the
 * exact window by isHigherCategoryOpenAt, which is what the server enforces.
 *
 * @param {Object} category - higher category from /highercategory/fetch
 * @param {Date} [at=new Date()] - the moment being ordered for
 * @returns {boolean}
 */
export const isHigherCategoryServedOn = (category, at = new Date()) => {
  const availability = category?.AvailabilityCopy || category?.Availability;
  if (!availability || typeof availability !== 'object') return true;

  const when = at instanceof Date && !Number.isNaN(at.getTime()) ? at : new Date();
  const day = availability[DAY_KEYS[when.getDay()]];

  return day?.isAvailable !== false;
};

/**
 * Parse a time string (HH:MM format) into minutes since midnight
 * @param {string} timeString - Time in HH:MM format
 * @returns {number} - Minutes since midnight
 */
function parseTimeString(timeString) {
  if (!timeString) return 0;

  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

export const getRestaurantStatus = (restaurant, type, pickupDateTime) => {
  if (!restaurant) return false;

  const curday = moment(pickupDateTime).format('ddd');
  const curtime = moment(pickupDateTime).format('HH:mm');

  if (restaurant?.availability?.[curday]) {
    if (restaurant.availability[curday].isAvailable === true) {
      //open 24 hours
      if (restaurant.availability[curday].start === restaurant.availability[curday].end) {
        return type === 'string' ? 'Open' : true;
      }

      const restaurantOpen = moment('2020-01-01 ' + restaurant.availability[curday].start);
      const restaurantClose = moment('2020-01-01 ' + restaurant.availability[curday].end);
      const _curDateTime = moment('2020-01-01 ' + curtime);

      // Check if time is continuos
      if (restaurantClose < restaurantOpen && _curDateTime > restaurantOpen) {
        return type === 'string' ? 'Open' : true;
      }

      if (_curDateTime >= restaurantOpen && _curDateTime <= restaurantClose) {
        return type === 'string' ? 'Open' : true;
      } else {
        if (restaurant.availability[curday].othertime) {
          let open = false;
          restaurant.availability[curday].othertime.forEach((time) => {
            const restaurantOpen2 = moment('2020-01-01 ' + time.start);
            const restaurantClose2 = moment('2020-01-01 ' + time.end);

            if (open) {
              return;
            }

            if (_curDateTime >= restaurantOpen2 && _curDateTime <= restaurantClose2) {
              open = true;
            }
          });

          if (open) {
            return type === 'string' ? 'Open' : true;
          }
        }

        return type === 'string' ? 'Closed' : false;
      }
    }
  }

  return type === 'string' ? 'Closed' : false;
};

export const getEarliestTime = (
  restaurant,
  lagTime = 30,
  interval = 10,
  selectedDate = null,
  selectedCategory,
) => {
  if (!restaurant || !restaurant.availability) {
    return '12:00'; // Default time
  }

  // Use provided date or today as default
  const date = selectedDate || new Date();
  const today = new Date();
  const isToday = date.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0);

  // Only round the current time if we're dealing with today's date
  let roundedTime = '00:00';
  if (isToday) {
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / lagTime) * lagTime, 0, 0);
    roundedTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(
      2,
      '0',
    )}`;
  }

  const disabledTimes = getDisabledTimes(date, restaurant, lagTime, interval, selectedCategory);
  const allTimeSlots = generateAllTimeSlots(lagTime, interval);
  const availableTimeSlots = allTimeSlots.filter((time) => !disabledTimes.includes(time));

  if (availableTimeSlots.length === 0) {
    // No available times for this date, return default
    return '12:00';
  }

  if (isToday) {
    // For today, filter out times in the past
    const futureAvailableSlots = availableTimeSlots.filter((time) => time >= roundedTime);

    if (futureAvailableSlots.length === 0) {
      // No future times available today
      return '12:00'; // Return default time
    }

    // Return first available future time
    return futureAvailableSlots[0];
  } else {
    // For future dates, return the first available time
    return availableTimeSlots[0];
  }
};

/**
 * Convert time string to minutes since midnight
 * @param {string} timeStr - Time in HH:MM format
 * @returns {number} - Minutes since midnight
 */
export const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Generate all possible time slots for a day
 * @param {number} lagTime - Lag time interval in minutes
 * @param {number} interval - Time interval in minutes
 * @returns {Array} - Array of all possible time slots
 */
export const generateAllTimeSlots = (lagTime = 30, interval = 10) => {
  const slots = [];
  const totalMinutesInDay = 24 * 60;
  const totalSlots = Math.floor(totalMinutesInDay / interval);

  for (let i = 0; i < totalSlots; i++) {
    const totalMinutes = i * interval;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    slots.push(`${formattedHours}:${formattedMinutes}`);
  }

  return slots;
};

/**
 * Add time slots between start and end times to the availableTimeSlots array
 * @param {Array} availableTimeSlots - Array to store available time slots
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @param {number} lagTime - Lag time interval in minutes
 * @param {number} interval - Time interval in minutes
 */
export const addTimeSlotsInRange = (availableTimeSlots, startTime, endTime, lagTime, interval) => {
  // Convert start and end times to minute values for easier comparison
  const startMinutes = timeToMinutes(startTime);
  let endMinutes = timeToMinutes(endTime);

  // Special handling for end times like 23:30 and 23:59
  // These should be treated as valid last order times, not closing times
  // If the end time is exactly 23:30 or 23:59, we want to include it
  if (endTime === '23:30' || endTime === '23:59') {
    // Don't adjust - these are valid order times
  }

  // Handle overnight hours (when end time is earlier than start time)
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60; // Add 24 hours
  }

  // Generate all possible time slots for the day
  const allTimeSlots = generateAllTimeSlots(lagTime, interval);

  // Add all time slots that fall within the range (inclusive)
  allTimeSlots.forEach((timeSlot) => {
    const slotMinutes = timeToMinutes(timeSlot);

    // Check if the slot falls within the range
    let isInRange = false;

    if (endMinutes > 24 * 60) {
      // Overnight range case
      isInRange = slotMinutes >= startMinutes || slotMinutes <= endMinutes - 24 * 60;
    } else {
      // Normal range case - ensure end time is INCLUSIVE
      // This is the key fix: we include the exact end time
      isInRange = slotMinutes >= startMinutes && slotMinutes <= endMinutes;
    }

    if (isInRange && !availableTimeSlots.includes(timeSlot)) {
      availableTimeSlots.push(timeSlot);
    }
  });
};

/**
 * Gets disabled time slots based on restaurant availability
 * @param {Date} date - Selected date
 * @param {Object} restaurant - Restaurant data object
 * @param {number} lagTime - Lag time interval in minutes (default: 30)
 * @param {number} interval - Time interval in minutes (default: 10)
 * @param {Object} selectedHigherCategory - Category object with availability information
 * @returns {Array} - Array of time strings that should be disabled
 */
export const getDisabledTimes = (
  date,
  restaurant,
  lagTime = 30,
  interval = 10,
  selectedHigherCategory,
) => {
  if (!date || !restaurant || !restaurant.availability) {
    return []; // No disabled times if missing required data
  }

  const dayOfWeek = moment(date).format('ddd');
  const dayAvailability = restaurant.availability[dayOfWeek];

  // If the day is not available in restaurant, disable all times
  if (!dayAvailability || !dayAvailability.isAvailable) {
    return generateAllTimeSlots(lagTime, interval); // All times are disabled
  }

  // Generate all possible time slots for the day
  const allTimeSlots = generateAllTimeSlots(lagTime, interval);
  const availableTimeSlots = [];

  // Check if selected date is today
  const today = moment().startOf('day');
  const selectedDay = moment(date).startOf('day');
  const isToday = selectedDay.isSame(today, 'day');

  // For today only: calculate the minimum time (current time + lagTime, rounded up)
  let minTimeForToday = null;
  // now + lagTime can land after midnight (22:50 + 60 min for delivery). Then
  // nothing left today is far enough ahead — comparing the "HH:mm" of
  // tomorrow's moment against today's slots would re-enable all of them.
  let todayIsOver = false;
  if (isToday) {
    const now = moment();
    const minMoment = now.clone().add(lagTime, 'minutes');
    // Round up to next interval
    const minutes = minMoment.minutes();
    const roundedMinutes = Math.ceil(minutes / interval) * interval;
    minMoment.minutes(roundedMinutes).seconds(0);
    minTimeForToday = minMoment.format('HH:mm');
    todayIsOver = !minMoment.isSame(today, 'day');
  }

  // The server's rule (isRestaurantOpenNow, mirrored by isAvailabilityOpenAt)
  // has the last word: /woltDelivery/check refuses a slot it calls closed. The
  // ranges above are more generous at the edges — a lone 00:00-23:30 day was
  // treated as open around the clock, and an overnight window counted its
  // early-morning tail on its own day — so a slot is only offered when both
  // agree.
  const dayStart = moment(date).startOf('day');
  const isOpenBySchedule = (time) => {
    const [hours, mins] = time.split(':').map(Number);
    const at = dayStart.toDate();
    at.setHours(hours, mins, 0, 0);
    return isAvailabilityOpenAt(restaurant.availability, at);
  };

  // Always add restaurant's primary time range (start to end)
  if (dayAvailability.start && dayAvailability.end) {
    addTimeSlotsInRange(
      availableTimeSlots,
      dayAvailability.start,
      dayAvailability.end,
      lagTime,
      interval,
    );
  }

  // Handle other time slots based on category availability
  if (dayAvailability.othertime && Array.isArray(dayAvailability.othertime)) {
    if (selectedHigherCategory?.Availability) {
      const categoryAvailability = selectedHigherCategory.Availability[dayOfWeek];

      // If category is available, intersect othertime with category times
      if (categoryAvailability && categoryAvailability.isAvailable) {
        const categoryTimeSlots = [];

        // Get category time slots
        if (categoryAvailability.start && categoryAvailability.end) {
          addTimeSlotsInRange(
            categoryTimeSlots,
            categoryAvailability.start,
            categoryAvailability.end,
            lagTime,
            interval,
          );
        }

        // Add category other time slots if available
        if (categoryAvailability.othertime && Array.isArray(categoryAvailability.othertime)) {
          categoryAvailability.othertime.forEach((slot) => {
            if (slot.start && slot.end) {
              addTimeSlotsInRange(categoryTimeSlots, slot.start, slot.end, lagTime, interval);
            }
          });
        }

        // For restaurant's othertime slots, only add times that are also available in category
        dayAvailability.othertime.forEach((slot) => {
          if (slot.start && slot.end) {
            const restaurantOtherTimeSlots = [];
            addTimeSlotsInRange(restaurantOtherTimeSlots, slot.start, slot.end, lagTime, interval);

            // Only add times that are in both restaurant othertime and category
            restaurantOtherTimeSlots.forEach((time) => {
              if (categoryTimeSlots.includes(time) && !availableTimeSlots.includes(time)) {
                availableTimeSlots.push(time);
              }
            });
          }
        });
      }
      // If category is not available, don't add any othertime slots
    } else {
      // No category restriction, add all restaurant othertime slots
      dayAvailability.othertime.forEach((slot) => {
        if (slot.start && slot.end) {
          addTimeSlotsInRange(availableTimeSlots, slot.start, slot.end, lagTime, interval);
        }
      });
    }
  }

  // Return disabled times (all times that are not available)
  // For today: also disable times that are in the past (before minTimeForToday)
  // For future dates: only disable times outside restaurant availability
  return allTimeSlots.filter((time) => {
    const isOutsideAvailability = !availableTimeSlots.includes(time) || !isOpenBySchedule(time);

    // For future dates, only check availability
    if (!isToday) {
      return isOutsideAvailability;
    }

    // For today, also disable past times
    const isPastTime = todayIsOver || time < minTimeForToday;
    return isOutsideAvailability || isPastTime;
  });
};

// Utility function to get earliest or latest time for a day
export const getDayTime = (availability, day, type, startOrEnd) => {
  if (!availability || !availability[day]) return null;

  const times = [`${availability[day].start} - ${availability[day].end}`];

  if (availability[day].othertime) {
    availability[day].othertime.forEach((item) => {
      times.push(`${item.start} - ${item.end}`);
    });
  }

  const sorted = times.sort();
  const timeIndex = type === 'earliest' ? 0 : sorted.length - 1;
  return sorted[timeIndex].split(' - ')[startOrEnd === 'start' ? 0 : 1];
};

// Process availability data for a day
export const processAvailabilityForDay = (availability, days, dayKey, dayLabel) => {
  if (!availability || !availability[dayKey] || !availability[dayKey].isAvailable) {
    return {
      dayLabel,
      isAvailable: false,
      timeSlots: [],
    };
  }

  // Handle single and multiple time slots
  let timeSlots = [];
  const currentDayData = availability[dayKey];

  // If there are no other time slots
  if (!currentDayData.othertime || currentDayData.othertime.length === 0) {
    let { start, end } = currentDayData;

    // Handle special case for 24-hour operations
    if (start === '00:00' && end === '23:59') {
      timeSlots.push({ start, end });
      return { dayLabel, isAvailable: true, timeSlots };
    }

    // Handle case where closing time is midnight (next day)
    if (end === '23:59') {
      const dayIndex = days.findIndex((d) => d.key === dayKey);
      const nextDayKey = days[(dayIndex + 1) % days.length].key;
      const nextDayEarliestStart = getDayTime(availability, nextDayKey, 'earliest', 'start');

      if (nextDayEarliestStart === '00:00') {
        const nextDayEarliestEnd = getDayTime(availability, nextDayKey, 'earliest', 'end');
        end = nextDayEarliestEnd;
      }
    }

    timeSlots.push({ start, end });
  } else {
    // Process multiple time slots
    const allTimeSlots = [
      { start: currentDayData.start, end: currentDayData.end },
      ...currentDayData.othertime.map((item) => ({ start: item.start, end: item.end })),
    ];

    // Sort time slots by start time
    const sortedTimeSlots = allTimeSlots.sort((a, b) => a.start.localeCompare(b.start));

    sortedTimeSlots.forEach((slot) => {
      const { start, end } = slot;

      // Check if start time is midnight and might be continuation from previous day
      if (start === '00:00') {
        const dayIndex = days.findIndex((d) => d.key === dayKey);
        const prevDayKey = days[(dayIndex - 1 + days.length) % days.length].key;
        const prevDayLatestEnd = getDayTime(availability, prevDayKey, 'latest', 'end');

        if (prevDayLatestEnd === '23:59') {
          return; // Skip this time slot as it's a continuation
        }
      }

      // Check if end time is midnight (next day)
      let finalEnd = end;
      if (end === '23:59') {
        const dayIndex = days.findIndex((d) => d.key === dayKey);
        const nextDayKey = days[(dayIndex + 1) % days.length].key;
        const nextDayEarliestStart = getDayTime(availability, nextDayKey, 'earliest', 'start');

        if (nextDayEarliestStart === '00:00') {
          const nextDayEarliestEnd = getDayTime(availability, nextDayKey, 'earliest', 'end');
          finalEnd = nextDayEarliestEnd;
        }
      }

      timeSlots.push({ start, end: finalEnd });
    });
  }

  return {
    dayLabel,
    isAvailable: true,
    timeSlots,
  };
};

// Component for displaying a single time slot
export const TimeSlot = ({ start, end }) => {
  // Format end time of 23:59 to display as 00:00 for user-friendliness
  const formattedEnd = end === '23:59' ? '00:00' : end;

  return (
    <span>
      {start} - {formattedEnd}
    </span>
  );
};

TimeSlot.propTypes = {
  start: PropTypes.string.isRequired,
  end: PropTypes.string.isRequired,
};
